<template>
  <article class="tool-page">
    <nav class="tool-page__breadcrumbs" aria-label="Breadcrumb">
      <router-link to="/console/tools">All tools</router-link>
      <span class="tool-page__breadcrumb-separator">/</span>
      <span>Standards</span>
    </nav>

    <header class="tool-page__header">
      <div class="tool-page__title-row">
        <h1>Standards</h1>
        <span class="console-badge console-badge--neutral console-badge--small">Free during beta</span>
      </div>
      <p class="tool-page__lead">
        A searchable library of coding standards, design patterns, and operational guidelines
        that external LLM agents can consult before they write code. Includes a one-line installer
        that registers an MCP server for Claude Code, Copilot, Cursor, Amazon Q, and Codex.
      </p>
    </header>

    <section class="tool-page__section">
      <h2>Quick start &mdash; install the MCP server</h2>
      <p>One-liner for Claude Code (the default):</p>
      <CodeBlock language="bash" :code="claudeInstall" />
      <p>For other assistants, add an <code>--assistant</code> flag:</p>
      <CodeBlock language="bash" :code="otherAssistantsInstall" />
      <p>
        The installer downloads a thin Python client, verifies SHA256 checksums, and registers the
        MCP server with your assistant. After install, tools like <code>search_standards</code>,
        <code>list_standards</code>, and <code>generate_claudemd</code> are immediately available
        in your agent.
      </p>
    </section>

    <section class="tool-page__section">
      <h2>Paste this into your agent</h2>
      <p>
        If you'd rather not install anything, paste the block below into your agent's chat
        window (or system prompt). The agent will know how to search standards, fetch the full
        text of any cited standard, and generate project-level scaffolding on demand.
      </p>
      <p class="tool-page__note">
        <strong>Tradeoff:</strong> the pasted instructions and every search result the agent
        pulls in sit in conversation context and get re-billed every turn. The MCP install
        keeps the tool surface outside the conversation &mdash; only the actual call and
        response cost tokens. Use paste-mode for one-off sessions; install the MCP for
        sustained work.
      </p>
      <CodeBlock language="markdown" :code="agentPrompt" />
    </section>

    <section class="tool-page__section">
      <h2>Available MCP tools</h2>
      <p>
        Every entry below is fetched live from <code>{{ toolsUrl }}</code> &mdash; if a tool ships
        or is renamed, it appears here without a site redeploy.
      </p>

      <div v-if="toolsState === 'loading'" class="tool-page__placeholder">
        Loading tools&hellip;
      </div>
      <div v-else-if="toolsState === 'error'" class="alert alert--error">
        Could not fetch tools from <code>{{ toolsUrl }}</code>: {{ toolsError }}
      </div>
      <table v-else class="tool-page__endpoints">
        <thead>
          <tr>
            <th scope="col">Tool</th>
            <th scope="col">When to use</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tool in toolsList" :key="tool.name">
            <td><code>{{ tool.name }}</code></td>
            <td>{{ tool.summary }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="tool-page__section">
      <h2>Direct HTTP &mdash; no install required</h2>
      <p>
        Every MCP tool is also reachable as a plain HTTP endpoint. Agents that don't speak MCP can
        call these from any runtime.
      </p>

      <h3>Search</h3>
      <CodeBlock language="bash" :code="searchExample" />
      <p>
        Returns a synthesized answer plus the underlying standard chunks. Query params:
        <code>top_k</code> (1&ndash;20, default 10), <code>synthesize</code> (bool, default true),
        <code>min_score</code> (0&ndash;1, default 0.25).
      </p>

      <h3>List and get</h3>
      <CodeBlock language="bash" :code="listGetExample" />
    </section>

    <section class="tool-page__section">
      <h2>Endpoints</h2>
      <table class="tool-page__endpoints">
        <thead>
          <tr>
            <th scope="col">Method &amp; path</th>
            <th scope="col">Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>GET /search?q=&lt;query&gt;</code></td>
            <td>Semantic search across all standards (synthesized answer + sources)</td>
          </tr>
          <tr>
            <td><code>GET /standards</code></td>
            <td>List every indexed standard (filter with <code>?technologies=node,lambda</code>)</td>
          </tr>
          <tr>
            <td><code>GET /standards/{chunk_id}</code></td>
            <td>Full content of a single standard</td>
          </tr>
          <tr>
            <td><code>GET /prompts</code></td>
            <td>List prompt templates (code-review, security-trifecta, etc.)</td>
          </tr>
          <tr>
            <td><code>GET /prompts/{prompt_id}</code></td>
            <td>Full prompt content with optional tech-stack filtering</td>
          </tr>
          <tr>
            <td><code>GET /skills</code></td>
            <td>List Claude Code / Copilot skill templates</td>
          </tr>
          <tr>
            <td><code>POST /skills/generate</code></td>
            <td>Generate a skill from a template, customized to a tech stack</td>
          </tr>
          <tr>
            <td><code>POST /claudemd/generate</code></td>
            <td>Generate a project <code>CLAUDE.md</code> tailored to a tech stack</td>
          </tr>
          <tr>
            <td><code>POST /gaps</code></td>
            <td>Report a missing or incomplete standard</td>
          </tr>
          <tr>
            <td><code>GET /mcp/tools</code></td>
            <td>Dynamic registry consumed by the thin client</td>
          </tr>
          <tr>
            <td><code>GET /health</code></td>
            <td>Dependency readiness (s3-vectors, embeddings, cache)</td>
          </tr>
        </tbody>
      </table>
      <p class="tool-page__note">
        All endpoints are public during beta. Geo-restricted to US/CA (returns
        <code>403 ERROR_GEO_BLOCKED</code> elsewhere). Free-tier query limits: 300 chars for
        <code>?q=</code>, 2000 chars for POST bodies.
      </p>
    </section>

    <section class="tool-page__section">
      <h2>Integration patterns</h2>

      <h3>Pattern 1 &mdash; Install the MCP server</h3>
      <p>
        Default for Claude Code and any assistant with native MCP support. Run the installer
        above; the MCP tools light up inside your agent and the API surface stays hidden behind
        them. Best for interactive coding sessions.
      </p>

      <h3>Pattern 2 &mdash; Curl from inside any agent</h3>
      <p>If your agent doesn't speak MCP, teach it to hit the search endpoint directly:</p>
      <CodeBlock language="markdown" :code="pattern2SystemPrompt" />

      <h3>Pattern 3 &mdash; Programmatic with structured outputs</h3>
      <p>
        For automated pipelines (CI checks, batch code review), fetch a prompt template and feed
        it to a JSON-Schema-aware decoder. The standards content stays in one place; your pipeline
        gets the version updates for free.
      </p>
      <CodeBlock language="typescript" :code="pattern3Code" />
    </section>
  </article>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import CodeBlock from '@/components/CodeBlock.vue'

const baseUrl = 'https://agentic.j2clark.info'
const toolsUrl = `${baseUrl}/mcp/tools`

const toolsList = ref([])
const toolsState = ref('loading')
const toolsError = ref('')

const claudeInstall = `curl -sSL ${baseUrl}/install.sh | bash`

const otherAssistantsInstall = `# GitHub Copilot
curl -sSL ${baseUrl}/install.sh | bash -s -- --assistant copilot

# Amazon Q
curl -sSL ${baseUrl}/install.sh | bash -s -- --assistant amazonq

# Cursor
curl -sSL ${baseUrl}/install.sh | bash -s -- --assistant cursor

# OpenAI Codex
curl -sSL ${baseUrl}/install.sh | bash -s -- --assistant codex`

const searchExample = `curl '${baseUrl}/search?q=dependency+injection'`

const listGetExample = `# List every standard
curl ${baseUrl}/standards

# Get a single standard by its chunk_id
curl ${baseUrl}/standards/testing-stubs-vs-mocks`

const agentPrompt = `You have access to the agentic-standards API at ${baseUrl}.

Before writing code, search for relevant standards and patterns:

  GET ${baseUrl}/search?q=<keywords>

The response includes a synthesized "answer" plus a list of source chunks
with chunk_ids. For the full text of any cited standard:

  GET ${baseUrl}/standards/<chunk_id>

Other useful endpoints:
  GET  ${baseUrl}/skills            — list Claude Code / Copilot skill templates
  POST ${baseUrl}/skills/generate   — generate a skill from a template
  POST ${baseUrl}/claudemd/generate — generate a project CLAUDE.md for a tech stack
  GET  ${baseUrl}/prompts           — list prompt templates (code-review, security, etc.)

If the user runs Claude Code or another MCP-aware assistant, suggest they install
the MCP server with a one-liner so these tools light up natively:

  curl -sSL ${baseUrl}/install.sh | bash

Always cite the chunk_id(s) you used so the user can verify the source.`

const pattern2SystemPrompt = agentPrompt

const pattern3Code = `import Anthropic from '@anthropic-ai/sdk';

const client = new Anthropic();

// Fetch a prompt template that already knows about the user's tech stack
const prompt = await fetch(
  '${baseUrl}/prompts/code-review?technologies=typescript,vue',
).then(r => r.json());

const result = await client.messages.create({
  model: 'claude-opus-4-7',
  max_tokens: 4096,
  system: prompt.content,
  messages: [{ role: 'user', content: diffToReview }],
});`

function summarizeDescription(desc) {
  if (!desc) return ''
  const stripped = desc.replace(/^\*\*[^*]+\*\*\s*/, '')
  const firstSentence = stripped.split(/\.(?:\s|$)/)[0].trim()
  return firstSentence ? `${firstSentence}.` : stripped
}

onMounted(async () => {
  try {
    const response = await fetch(toolsUrl, { headers: { Accept: 'application/json' } })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    const data = await response.json()
    toolsList.value = (data.tools || []).map((tool) => ({
      name: tool.name,
      summary: summarizeDescription(tool.description),
    }))
    toolsState.value = 'ready'
  } catch (err) {
    toolsError.value = err instanceof Error ? err.message : String(err)
    toolsState.value = 'error'
  }
})
</script>

<style src="@/styles/console.css"></style>
<style src="@/styles/tool-page.css"></style>
